<i18n src="./resources/locales.json"></i18n>

<template>
  <div ref="contact" class="contact" :class="{ expanded }">
    <div
      class="contact-container"
      @contextmenu.prevent="!expanded && openContactMenu($event)"
      @click="$parent.$parent.toggleExpandedView()"
    >
      <div class="avatar">
        <EcsAvatar :contact-id="contact.id" :alt="contact.displayName" :size="48" />
      </div>

      <div class="details">
        <div v-if="!!contact.notes.length" class="notes-badge">
          <v-icon small>mdi-note-multiple</v-icon>
        </div>
        <div class="name" v-once>{{ contact.displayName }}</div>
        <div class="endpoint-detail">
          <v-icon>{{ contact.phones.length ? 'mdi-phone' : contact.email ? 'mdi-email' : '' }}</v-icon>
          {{ contact.phones.length ? contact.phones[0] : contact.email }}
        </div>
      </div>

      <div v-if="expanded" class="menu-action">
        <v-menu bottom left>
          <template v-slot:activator="{ on, attrs }">
            <v-btn icon v-bind="attrs" v-on="on">
              <v-icon>mdi-dots-vertical</v-icon>
            </v-btn>
          </template>

          <v-list>
            <v-list-item v-for="(action, i) in contactActions" :key="i" @click="action.action">
              <v-list-item-title>{{ action.label }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </div>
    </div>

    <v-progress-linear
      v-if="isLoading"
      indeterminate
      height="3"
      color="var(--v-surface-base)"
      style="margin-top: -3px"
    />

    <v-divider v-show="expanded"></v-divider>

    <div>
      <v-expand-transition>
        <div v-if="expanded">
          <v-tabs v-model="tab" height="40" background-color="transparent">
            <v-tab>{{ $t('tabs.contactInfo') }}</v-tab>
            <v-tab v-if="contact.infoFields.length">{{ $t('tabs.infoFields') }}</v-tab>
            <v-tab>{{ $t('tabs.notes') }}</v-tab>
          </v-tabs>

          <v-tabs-items v-model="tab" class="tab-content">
            <v-tab-item>
              <EcsContactForm :contact="contact" />
            </v-tab-item>

            <v-tab-item v-if="contact.infoFields.length">
              <div class="infoFields">
                <div class="infoField" v-for="(note, index) in contact.infoFields" :key="index">
                  {{ note }}
                </div>
              </div>
            </v-tab-item>

            <v-tab-item>
              <v-list-item>
                <v-list-item-avatar>
                  <EcsAvatar
                    :agent-id="userInfo && userInfo.id"
                    :alt="userInfo ? userInfo.name : username"
                    :size="40"
                    no-animation
                  />
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-text-field
                    v-model="newNote"
                    :placeholder="$t('placeholders.newNote')"
                    :disabled="addNewNoteLoading || userRole === 'OrgAdmin'"
                    @keypress.enter="addNote"
                  />
                </v-list-item-content>
                <v-list-item-action>
                  <v-btn icon @click="addNote" :loading="addNewNoteLoading">
                    <v-icon>mdi-send</v-icon>
                  </v-btn>
                </v-list-item-action>
              </v-list-item>
              <v-list color="transparent" flat dense>
                <v-list-item v-for="note in contact.notes" :key="note.id + note.createTime" class="note">
                  <v-list-item-avatar>
                    <EcsAvatar :agent-id="note.creator.id" :alt="note.creator.displayName" :size="40" no-animation />
                  </v-list-item-avatar>
                  <v-list-item-content>
                    <v-list-item-title>
                      <span class="name">{{ note.creatorName }}</span>
                      <span class="date"> &bull; {{ formatNoteDate(note.createTime) }}</span>
                    </v-list-item-title>
                    <!-- eslint-disable-next-line vue/no-v-html -->
                    <div v-html="note.note" class="content"></div>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-tab-item>
          </v-tabs-items>
        </div>
      </v-expand-transition>
    </div>
  </div>
</template>

<script>
import EcsAvatar from '@/components/ecs-avatar/EcsAvatar';
import DateTime from 'luxon/src/datetime';
import { mapActions, mapGetters } from 'vuex';
import { CrmService } from '@/services';
import EcsContactForm from '@/components/ecs-contact-form/EcsContactForm';

export default {
  name: 'EcsContactCard',

  components: {
    EcsContactForm,
    EcsAvatar
  },

  props: {
    expanded: {
      type: Boolean,
      default: false
    },
    contact: {
      type: Object,
      default: null
    }
  },

  data() {
    return {
      tab: null,
      isLoading: false,
      newNote: '',
      addNewNoteLoading: false,
      contactActions: [
        {
          label: 'Delete',
          action: () => {
            this.$root.$emit('open-confirm-dialog', {
              title: this.$t('actions.delete.confirm.title'),
              message: this.$t('actions.delete.confirm.message', { name: this.contact.displayName }),
              action: this.deleteContact
            });
          }
        },
        {
          label: 'Add to List',
          action: () => {
            this.$root.$emit('open-list-selection-dialog', { action: this.assignToList });
          }
        }
      ]
    };
  },

  computed: {
    ...mapGetters(['userInfo', 'username', 'userRole'])
  },

  created() {},

  methods: {
    ...mapActions(['retrieveContactNotes', 'removeContact', 'snackbarSuccessMessage', 'searchLists', 'searchContacts']),

    retrieveContactInfo() {
      const contactInfo = [this.retrieveContactNotes(this.contact.id)];

      this.isLoading = true;
      Promise.all(contactInfo).finally(() => {
        this.isLoading = false;
      });
    },

    formatNoteDate(date) {
      return DateTime.fromISO(date).toLocaleString(DateTime.DATETIME_FULL);
    },

    deleteContact(confirmed) {
      if (confirmed) {
        return CrmService.deleteContact(this.contact.id).then(() => {
          this.snackbarSuccessMessage(this.$t('actions.delete.success', { name: this.contact.displayName }));
          this.removeContact(this.contact.id);
        });
      } else {
        return Promise.resolve();
      }
    },

    addNote() {
      this.addNewNoteLoading = true;
      CrmService.addNote(this.contact.id, this.newNote)
        .then(this.retrieveContactInfo)
        .finally(() => {
          this.addNewNoteLoading = false;
          this.newNote = '';
        });
    },

    assignToList(selection) {
      if (selection.length === 0) {
        return;
      }

      this.isLoading = true;
      const list = selection[0];
      CrmService.assignContactToList(this.contact.id, list.id)
        .then(() => {
          this.snackbarSuccessMessage(
            this.$t('actions.list.add.success', {
              name: this.contact.displayName,
              listName: list.name
            })
          );
          this.searchLists('');
        })
        .finally(() => (this.isLoading = false));
    },

    openContactMenu(event) {
      this.$root.$emit('open-context-menu', {
        menu: this.contactActions,
        position: {
          x: event.clientX,
          y: event.clientY
        }
      });
    }
  }
};
</script>

<style scoped src="./scoped.scss" lang="scss" />
